import { Box, Checkbox, FormControlLabel, Grid, Typography } from '@material-ui/core';
import { useBorrowerPortalMediaQueries } from '@roc/feature-app-core';
import {
  Button,
  Paper,
  PaperBorderType,
  SignatureField,
  SignatureFieldHandle,
} from '@roc/ui';
import { useStore } from '@roc/client-portal-shared/hooks';
import { observer } from 'mobx-react';
import { useEffect, useRef, useState } from 'react';
import { renderToString } from 'react-dom/server';
import { CAF_PDF_HTML } from '../caf-pdf';
import { TitleComp } from './titleComp';

export const getConfirmCAFEmail = (borrowerName: string) => {
  return (
    <>
      <p>Hello {borrowerName},</p>
      <br></br>
      <p>Your authorization has been successfully submitted!</p>
      <br></br>
      <p>You can find the signed form in the attached file.</p>
      <br></br>
      <p>Thank you.</p>
    </>
  );
};

export const ConsentAgreementText = () => (
  <Typography variant="body1" style={{ fontStyle: 'italic' }}>
    By proceeding, you authorize and permit CCredit Checker ("Lender"), in its
    own name or under any trade, assumed, or fictitious name (“doing business
    as” or “DBA”), along with its designees, agents, brokers, participating
    lenders, vendors, affiliates, and representatives ("Lender Parties"), to:
    <br />
    <ol style={{ margin: '16px 32px' }}>
      <li>Verify your past and present credit and background history.</li>
      <li>
        Order a consumer credit report (hard or soft), a business credit report
        (if applicable), and/or a background check, including an investigative
        consumer report (each referred to as a "Consumer Report").
      </li>
    </ol>
    You also authorize Lender and the Lender Parties to share your Consumer
    Report (or any information extracted from it) with Lender’s or any Lender
    Party’s financing sources, loan providers, brokers, participating lenders,
    successors, assigns, affiliates, agents, and representatives.
    <br />
    <br />
    Additionally, you authorize Lender and the Lender Parties to obtain updated
    credit and background verifications and order updated Consumer Reports
    periodically in accordance with Lender’s internal policies.
    <br />
    <br />
    You understand and acknowledge that neither Lender nor any Lender Party is
    responsible for the information contained in a Consumer Report, including
    any negative findings. You release Lender and all Lender Parties, including
    their employees, officers, and owners, from any liability related to
    obtaining or sharing such information.
    <br />
    <br />
    You also acknowledge that Consumer Reports and any information extracted
    from them will only be used in connection with the loan or credit
    application you are submitting.
    <br />
    <br />
    A photocopy, scan, or facsimile copy of this Authorization to Obtain
    Consumer and Credit Reports will be treated as the original.
    <br />
    <br />
    Under the federal Fair Credit Reporting Act, if any adverse action is taken
    based on your Consumer Report, you may request a copy of the report and a
    summary of your rights.
  </Typography>
);

interface CreditAuthorizationSignatureStepProps {
  requestToken: string;
  personalInfo?: any;
  emailAddress?: string;
  onComplete?: () => void;
  submitLabel?: string;
}

export const CreditAuthorizationSignatureStep = observer(
  ({
    requestToken,
    personalInfo,
    emailAddress,
    onComplete,
    submitLabel = 'Submit',
  }: CreditAuthorizationSignatureStepProps) => {
    const {
      globalStore,
      publicCreditAuthorizationStore,
      requestCreditAuthStore,
    } = useStore();
    const { isLargeScreen } = useBorrowerPortalMediaQueries();
    const signatureRef = useRef<SignatureFieldHandle>(null);
    const [agreed, setAgreed] = useState(false);
    const [signature, setSignature] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
      if (personalInfo) {
        publicCreditAuthorizationStore.personalInformationFormStore.loadForm({
          ...personalInfo,
          emailAddress: emailAddress ?? personalInfo.emailAddress,
        });
      }
      if (requestToken) {
        requestCreditAuthStore.borrowerDetailsVerification = {
          ...requestCreditAuthStore.borrowerDetailsVerification,
          requestToken,
        };
      }
    }, [requestToken]);

    const handleSubmit = async () => {
      if (submitting) {
        return;
      }

      if (!signatureRef.current?.isSigned()) {
        globalStore.notificationStore.showWarningNotification({
          message: 'Please sign the authorization before submitting it.',
        });
        signatureRef.current?.scrollTo();
        return;
      }

      const signatureURL = signatureRef.current.getSignature();
      const formData = publicCreditAuthorizationStore.personalInformationFormStore.getFormValues();

      setSignature(signatureURL);
      setSubmitting(true);

      try {
        await publicCreditAuthorizationStore.savePdfOnDropbox(async () => {
          await requestCreditAuthStore.saveSignatureAndPullCredit(
            signatureURL,
            formData,
            publicCreditAuthorizationStore.getPdf,
            renderToString(
              getConfirmCAFEmail(`${formData.firstName} ${formData.lastName}`)
            )
          );
          if (onComplete) {
            onComplete();
          }
        });
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <Box pb={2}>
        <div id="screenshotParent" style={{ display: 'none' }}>
          <CAF_PDF_HTML
            publicCreditAuthorizationStore={publicCreditAuthorizationStore}
            signature={signature}
            setContainerRef={() => {}}
          />
        </div>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12}>
            <Grid item xs={12}>
              <Box mb={3}>
                <TitleComp title="Consent Agreement" />
              </Box>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={12}>
            <Grid item xs={12}>
              <Box mb={3}>
                <Paper
                  variant="outlined"
                  style={{
                    maxHeight: isLargeScreen ? 300 : 150,
                    overflowY: 'auto',
                    padding: 24,
                    borderColor: '#ccc',
                  }}
                  borderType={PaperBorderType.SOLID_BORDER}
                >
                  <ConsentAgreementText />
                </Paper>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={agreed}
                      onChange={e => setAgreed(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box fontSize={18}>
                      I authorize and permit CCredit Checker to verify my credit
                      history and obtain a credit report as part of this
                      transaction.
                      <Box color={'red'} component={'span'} ml={0.5}>
                        *
                      </Box>
                    </Box>
                  }
                />
              </Box>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={12}>
            <Grid item xs={12}>
              <Box my={3}>
                <TitleComp
                  title={
                    <span>
                      Signature
                      <Box color="red" component={'span'} ml={0.5}>
                        *
                      </Box>
                    </span>
                  }
                />
              </Box>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12}>
                <SignatureField ref={signatureRef} penColor="green" />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" justifyContent="center" mt={2}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                testId="submitCreditAuthorization"
                disabled={!agreed || submitting}
                onClick={handleSubmit}
              >
                {submitLabel}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  }
);
